// Market panel tab-strip renderer, extracted from market-ui.ts at
// Phase 5 Step 19 to free space for the refresh button under the
// §10 400-line cap. Pure PixiJS render helper: builds a tab strip
// into the provided parent and returns the container so the caller
// can dispose it before re-rendering.

import { Container, Graphics, Text } from "pixi.js";

export type MarketTab = "listings" | "npc" | "history" | "repair";

export const TAB_KEYS: readonly MarketTab[] = ["listings", "npc", "history", "repair"] as const;

export const TAB_LABELS: Record<MarketTab, string> = {
  listings: "Listings",
  npc: "NPC Shop",
  history: "History",
  repair: "Repair",
};

const TAB_STRIP_H = 36; // contracts:allow-literal — tab bar height px
const TAB_LABEL_PAD_X = 18; // contracts:allow-literal — horizontal padding inside a tab button
const PAD = 12; // contracts:allow-literal — left margin to first tab
const TAB_GAP = 4; // contracts:allow-literal — px between tabs

const TAB_BG_INACTIVE = 0x1a1a3a;
const TAB_BG_ACTIVE = 0x2a2a55;
const TAB_TEXT_INACTIVE = 0x8888aa;
const TAB_TEXT_ACTIVE = 0xeeeeff;
const TAB_FONT_SIZE = 13; // contracts:allow-literal

export const TAB_STRIP_HEIGHT = TAB_STRIP_H;

export function buildMarketTabStrip(opts: {
  parent: Container;
  x: number;
  y: number;
  activeTab: MarketTab;
  onTabClick: (tab: MarketTab) => void;
}): Container {
  const { parent, x, y, activeTab, onTabClick } = opts;
  const strip = new Container();
  strip.x = x;
  strip.y = y;
  parent.addChild(strip);

  let cursor = PAD;
  for (const key of TAB_KEYS) {
    const isActive = key === activeTab;
    const label = new Text({
      text: TAB_LABELS[key],
      style: {
        fontSize: TAB_FONT_SIZE,
        fill: isActive ? TAB_TEXT_ACTIVE : TAB_TEXT_INACTIVE,
        fontFamily: "monospace",
        fontWeight: isActive ? "bold" : "normal",
      },
    });
    const btnW = label.width + TAB_LABEL_PAD_X * 2; // contracts:allow-literal — symmetric L+R padding

    const btn = new Container();
    btn.x = cursor;
    btn.y = 0;
    btn.eventMode = "static";
    btn.cursor = "pointer";

    const bg = new Graphics()
      .roundRect(0, 0, btnW, TAB_STRIP_H, 4) // contracts:allow-literal — corner radius
      .fill({ color: isActive ? TAB_BG_ACTIVE : TAB_BG_INACTIVE });
    btn.addChild(bg);

    label.x = TAB_LABEL_PAD_X;
    label.y = Math.round((TAB_STRIP_H - label.height) / 2); // contracts:allow-literal
    btn.addChild(label);

    btn.on("pointerdown", () => onTabClick(key));
    strip.addChild(btn);

    cursor += btnW + TAB_GAP;
    // Future: if tabs overflow strip width, add horizontal-scroll or wrap here.
  }
  return strip;
}
